/**
 * Which way the phone points, for the camera view (amendment 18).
 *
 * The camera view needs an *absolute* heading — from north, not from wherever the phone
 * happened to point when the page loaded — and browsers hand one out in three ways:
 *
 *   * Chrome, on Android and in the APK's WebView, fires `deviceorientationabsolute`.
 *   * Firefox for Android fires the ordinary `deviceorientation` with `absolute` set.
 *   * Safari on iOS fires the ordinary event with a relative alpha, adds the compass heading
 *     as `webkitCompassHeading`, and fires nothing at all until the page has asked —
 *     `DeviceOrientationEvent.requestPermission()`, from inside a tap.
 *
 * Anything else has no compass this page can use, and the camera view says so rather than
 * point at the wrong street. The angles become axes in `domain/viewfinder.ts`.
 */

import { alphaFromHeading, type Basis, basisFrom } from '$domain/viewfinder.ts';

/** How long to wait for a first reading with a north in it before calling the compass missing. */
const PATIENCE_MS = 2500;

type SafariOrientation = DeviceOrientationEvent & { webkitCompassHeading?: number };
type AskingOrientation = { requestPermission?: () => Promise<'granted' | 'denied'> };

/**
 * Ask for the compass where the browser wants to be asked — Safari, which insists on a tap —
 * and true everywhere else, where nobody asks. Call it from the tap that opens the view.
 */
export async function askForCompass(): Promise<boolean> {
  if (typeof DeviceOrientationEvent === 'undefined') return true;
  const asking = DeviceOrientationEvent as unknown as AskingOrientation;
  if (asking.requestPermission === undefined) return true;
  try {
    return (await asking.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

/**
 * Hand over the phone's attitude on every reading, or `undefined` once if nothing with a north
 * in it arrives in time. Returns the function that stops listening.
 */
export function watchCompass(onReading: (basis: Basis | undefined) => void): () => void {
  let heard = false;
  const type = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';

  const read = (event: Event) => {
    const { alpha, beta, gamma, absolute } = event as SafariOrientation;
    if (beta === null || gamma === null) return;
    const heading = (event as SafariOrientation).webkitCompassHeading;
    let north: number | undefined;
    if (typeof heading === 'number' && heading >= 0) {
      north = alphaFromHeading(heading, beta, gamma);
    } else if (alpha !== null && (absolute || event.type === 'deviceorientationabsolute')) {
      north = alpha;
    }
    // A relative alpha is no use at all: the columns would stand where the phone pointed at
    // the start, which is to say anywhere.
    if (north === undefined) return;
    heard = true;
    onReading(basisFrom(north, beta, gamma));
  };

  window.addEventListener(type, read);
  const timer = setTimeout(() => {
    if (!heard) onReading(undefined);
  }, PATIENCE_MS);
  return () => {
    clearTimeout(timer);
    window.removeEventListener(type, read);
  };
}
